import React, { useState } from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { SecondaryTag } from '@grnhse/seedling/lib/birch/components/tags';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import {
  NoPaddingStrongNoIcon as DefinitelyNotIcon,
  NoPaddingNoIcon as NoIcon,
  NoPaddingYesIcon as YesIcon,
  NoPaddingStrongYesIcon as StrongYesIcon,
} from 'shared/components/scorecard_icons/base_icons';
import QuestionIcon from '@grnhse/seedling/lib/birch/icons/question';
import { withLocalePrefix } from 'shared/utils/translation';
import InternalFeedbackModal, {
  type RatingValue,
} from 'talent_matching/components/modals/internal_feedback_modal';
import { DISCOVERY_SOURCE } from 'talent_matching/components/shared/discovery_source';
import { MatchStrength } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.internal_feedback');

type RatingOption = {
  value: RatingValue;
  labelKey: string;
  Icon: React.ComponentType<{ title?: string }>;
};

const ratingOptions: RatingOption[] = [
  {
    value: 'definitely_not',
    labelKey: 'ratings.definitely_not',
    Icon: DefinitelyNotIcon,
  },
  { value: 'no', labelKey: 'ratings.no', Icon: NoIcon },
  { value: 'yes', labelKey: 'ratings.yes', Icon: YesIcon },
  { value: 'strong_yes', labelKey: 'ratings.strong_yes', Icon: StrongYesIcon },
  {
    value: 'undecided',
    labelKey: 'ratings.undecided',
    Icon: () => <QuestionIcon size="xs" />,
  },
];

const BannerContainer = styled.div`
  border: 1px solid ${FullPalette.GREEN_E100};
  background-color: ${FullPalette.GREEN_E50};
  border-radius: ${Tokens.spacing['1']};
  padding: ${Tokens.spacing['3']};
`;

const PillButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing['0.5']};
  padding: ${Tokens.spacing['0.5']} ${Tokens.spacing['1.5']};
  border: 1px solid ${FullPalette.GREEN_500};
  border-radius: ${Tokens.spacing['1']};
  background: ${FullPalette.WHITE};
  cursor: pointer;
  white-space: nowrap;
  font-size: 13px;
  color: ${FullPalette.GREEN_700};

  &:hover {
    background: ${FullPalette.GREEN_100};
  }
`;

const PillIconWrapper = styled.span`
  display: flex;
  align-items: center;

  svg {
    height: 16px;
    width: 16px;
  }
`;

const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Tokens.spacing['0.5']};
`;

type Props = {
  candidateName: string;
  candidateId: number;
  hiringPlanId: number;
  userId: number;
  matchStrength: MatchStrength | null;
};

function storageKey(userId: number, hiringPlanId: number) {
  return `mygh_feedback_submitted_${userId}_${hiringPlanId}_${DISCOVERY_SOURCE}`;
}

function getRatedCandidates(userId: number, hiringPlanId: number): number[] {
  try {
    return JSON.parse(
      localStorage.getItem(storageKey(userId, hiringPlanId)) || '[]'
    );
  } catch {
    return [];
  }
}

function markCandidateRated(
  userId: number,
  hiringPlanId: number,
  candidateId: number
) {
  const rated = getRatedCandidates(userId, hiringPlanId);
  if (!rated.includes(candidateId)) {
    rated.push(candidateId);
    localStorage.setItem(
      storageKey(userId, hiringPlanId),
      JSON.stringify(rated)
    );
  }
}

const FeedbackBanner = ({
  candidateName,
  candidateId,
  hiringPlanId,
  userId,
  matchStrength,
}: Props) => {
  const [selectedRating, setSelectedRating] = useState<RatingValue | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(() =>
    getRatedCandidates(userId, hiringPlanId).includes(candidateId)
  );

  const handleRatingClick = (rating: RatingValue) => {
    setSelectedRating(rating);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSuccess = () => {
    markCandidateRated(userId, hiringPlanId, candidateId);
    setFeedbackSubmitted(true);
  };

  if (feedbackSubmitted) {
    return (
      <BannerContainer>
        <Stack spacing={0.5}>
          <Body medium>{t('banner.success_heading')}</Body>
          <SecondaryBody>{t('banner.success_description')}</SecondaryBody>
        </Stack>
      </BannerContainer>
    );
  }

  return (
    <>
      <BannerContainer>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Body medium>{t('banner.heading')}</Body>
            <SecondaryTag text={t('banner.tag')} />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <SecondaryBody>{t('banner.description')}</SecondaryBody>
            <HelpTooltip
              label={
                <Stack spacing={1}>
                  <Body medium>{t('banner.tooltip.heading')}</Body>
                  <SecondaryBody>{t('banner.tooltip.body_1')}</SecondaryBody>
                  <SecondaryBody>{t('banner.tooltip.body_2')}</SecondaryBody>
                  <SecondaryBody>{t('banner.tooltip.body_3')}</SecondaryBody>
                </Stack>
              }
              ariaLabel={t('banner.tooltip.heading')}
            />
          </Stack>
          <PillsRow>
            {ratingOptions.map(({ value, labelKey, Icon }) => (
              <PillButton
                key={value}
                type="button"
                onClick={() => handleRatingClick(value)}
              >
                <PillIconWrapper>
                  <Icon />
                </PillIconWrapper>
                {t(labelKey)}
              </PillButton>
            ))}
          </PillsRow>
        </Stack>
      </BannerContainer>

      {selectedRating && (
        <InternalFeedbackModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
          candidateName={candidateName}
          candidateId={candidateId}
          hiringPlanId={hiringPlanId}
          selectedRating={selectedRating}
          matchStrength={matchStrength}
        />
      )}
    </>
  );
};

export default FeedbackBanner;
